import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import WarningIcon from 'ui-library/icons/WarningIcon';
import Modal from 'ui-library/modals/Modal';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {firstColumn} from '../SharedStyles';

export default function DisplayClientSecretModal({
  clientSecret,
  onRequestClose,
  onRequestSubmit,
}: {
  clientSecret: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}): JSX.Element {
  const [showKey, setShowKey] = React.useState(false);
  const showOrHideKey = (): void => {
    setShowKey(!showKey);
  };
  return (
    <Modal
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      title="Generated Client Secret"
      buttons={[
        {
          text: 'Okay',
          submit: true,
          style: ButtonStyle.SECONDARY,
          dataTestAttrs: {component: 'DisplayClientSecretModal', element: 'okButton'},
        },
      ]}
    >
      <div className={classNames('u-spacing-top container', css(styles.tableWrapper))}>
        <Row className={classNames('u-lr-split', css(styles.grayBox))}>
          <WarningIcon className="u-tiny-spacing-right" />
          <span>
            <strong>
              Copy the following secret down before closing this modal. The secret isn’t stored and can’t be
              retrieved later.
            </strong>
          </span>
        </Row>
        <Row>
          <Col md={8} className={css(styles.pullLeft)}>
            <strong>Client Secret</strong>
          </Col>
        </Row>
        <Row>
          <Col md={6} className={classNames('u-width-full', css(styles.grayBox))}>
            <span
              id="__client-secret"
              {...getTestProps({component: 'DisplayClientSecretModal', element: 'clientSecret'})}
            >
              {showKey ? (
                <span>{clientSecret}</span>
              ) : (
                <span className={css(styles.secret)}>• • • • • • • • • • • • • • • • • • • •</span>
              )}
            </span>
            <div className="pull-right">
              <Tooltip container="body" placement={TooltipPlacement.TOP} title="Copy Client Secret">
                <Button
                  buttonStyle={ButtonStyle.CIRCLE_ICON}
                  size={ButtonSize.MINI}
                  className="u-spacing-left"
                  icon="copy"
                  onClick={() =>
                    copyToClipboard(clientSecret, {
                      showToastOnSuccess: true,
                      toastText: 'Copied Client Secret to clipboard.',
                    })
                  }
                />
              </Tooltip>
              <Tooltip
                container="body"
                placement={TooltipPlacement.TOP}
                title={showKey ? 'Hide Client Secret' : 'Show Client Secret'}
              >
                <Button
                  buttonStyle={ButtonStyle.CIRCLE_ICON}
                  size={ButtonSize.SMALL}
                  className="u-mini-spacing-left"
                  icon={showKey ? 'eye-slash' : 'eye'}
                  onClick={showOrHideKey}
                  dataTestAttrs={{component: 'DisplayClientSecretModal', element: 'revealButton'}}
                />
              </Tooltip>
            </div>
          </Col>
        </Row>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  secretContainer: firstColumn,
  secret: {
    fontSize: 20,
  },
  pullLeft: {
    paddingLeft: 0,
  },
  tableWrapper: {
    marginLeft: 0,
  },
  noRightPadding: {
    paddingRight: 0,
  },
  grayBox: {
    backgroundColor: GRAY_LIGHTER,
    padding: 10,
  },
});
